import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "汇总优先，不做标题党",
    desc: "每一条栏目内容都尽量交代时间、来源与背景，让读者三分钟之内就能掌握来龙去脉，而不是被断章取义的标题牵着走。",
  },
  {
    icon: "ri-scales-3-line",
    title: "多方信息，交叉核对",
    desc: "同一件事如果只有单一来源，我们会在文末标注「未经更多渠道印证」，避免把推测写成结论。",
  },
  {
    icon: "ri-leaf-line",
    title: "轻量阅读，尊重时间",
    desc: "日报以短条目为主，长内容才做分节目录。我们相信克制的信息密度，比堆砌的热闹更有价值。",
  },
];

const principles = [
  "只整理公开传播的娱乐资讯，不发布任何非公开个人信息。",
  "对尚未证实的说法一律标注来源与不确定性，不替读者下结论。",
  "文章涉及具体个人时，使用中性表述，避免人身攻击与污名化词汇。",
  "收到合理的内容异议后，会在核实的第一时间更正或下线相关条目。",
];

const voices = [
  {
    name: "夜猫子阿哲",
    role: "追更 11 个月的读者",
    text: "栏目分类太省事了，我只订阅影视剧透和综艺风波，每天早上一封邮件就把重点看完了。",
  },
  {
    name: "通勤读报人",
    role: "日报订阅用户",
    text: "最喜欢的是它会标清楚哪些是猜测、哪些有实锤，不用自己再去翻半天评论区。",
  },
  {
    name: "小满",
    role: "内容从业者",
    text: "写稿前会来这里过一遍当天的公开信息，时间线整理得比自己搜要快很多。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们｜黑料今日·娱乐吃瓜日报的定位与内容原则",
    description:
      "了解黑料今日：一个每日更新的娱乐吃瓜日报。这里介绍我们的网站定位、发展理念、内容原则与读者口碑，以及联系方式。",
    keywords: "黑料今日,关于我们,网站定位,内容原则,娱乐日报",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于黑料今日",
        url: "https://043r0.cn/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "黑料今日",
        url: "https://043r0.cn/",
        email: "heyliao@example.com",
        description: "每日更新的娱乐吃瓜日报，热门爆料与精选汇总一站看全。",
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="px-4 md:px-8 pt-8 md:pt-10 pb-8 bg-background-100 border-b border-background-200">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-5 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <h1 className="font-heading font-black text-3xl md:text-5xl text-foreground-950 leading-tight">
                关于黑料今日
              </h1>
              <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
                我们是一个专注娱乐资讯整理的日报站点。每天把热门讨论梳理成短小的条目，
                按栏目归档，让关注娱乐动态的人不必在信息流里反复翻找。
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/daily"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-newspaper-line" />
                  阅读最新日报
                </Link>
                <Link
                  to="/faq"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-foreground-300 text-foreground-800 font-medium hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-question-line" />
                  查看常见问题
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <figure className="rounded-lg overflow-hidden h-56 md:h-72 bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20workspace%20with%20vintage%20typewriter%20and%20stacked%20newspapers%20on%20warm%20wooden%20desk%2C%20soft%20morning%20light%2C%20no%20people%2C%20amber%20and%20cream%20tones%20photography&width=1000&height=700&seq=hl-about-01&orientation=landscape"
                  alt="黑料今日编辑部的日常整理场景"
                  title="黑料今日 编辑部"
                  className="w-full h-full object-cover object-top"
                />
              </figure>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-8 py-14 md:py-20">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="text-xs font-medium text-primary-600 tracking-[0.2em]">POSITIONING</span>
              <h2 className="mt-2 font-heading font-black text-2xl md:text-3xl text-foreground-950">
                网站定位
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                黑料今日不是一个「爆猛料」的地方，而是一个整理信息的地方。娱乐话题每天层出不穷，
                但真正有价值的部分往往散落在不同的平台与时段里。我们把它们收集起来，按栏目归类，
                标注清楚时间与背景，让读者用最少的时间获得最完整的信息轮廓。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
                站点面向关注娱乐动态、习惯碎片化阅读的用户。既可以在首页浏览当日精选，
                也可以只订阅自己关心的栏目，把日报当作一份可以随时查阅的娱乐信息索引。
              </p>
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { k: "286", v: "期已发布" },
                  { k: "8", v: "个固定栏目" },
                  { k: "09:00", v: "每日更新时间" },
                  { k: "3 分钟", v: "平均读完时长" },
                ].map((s) => (
                  <div key={s.v} className="p-4 rounded-lg bg-background-100 border border-background-200">
                    <p className="font-heading font-bold text-xl text-foreground-950">{s.k}</p>
                    <p className="text-xs text-foreground-500 mt-1">{s.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-7 rounded-full bg-primary-500" />
            <h2 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
              发展理念
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {values.map((v) => (
              <div
                key={v.title}
                className="p-6 rounded-lg bg-background-50 border border-background-200"
              >
                <span className="w-12 h-12 rounded-md bg-accent-100 flex items-center justify-center">
                  <i className={`${v.icon} text-2xl text-accent-800`} />
                </span>
                <h3 className="mt-5 font-heading font-bold text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-8 py-14 md:py-20">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="text-xs font-medium text-primary-600 tracking-[0.2em]">PRINCIPLES</span>
              <h2 className="mt-2 font-heading font-black text-2xl md:text-3xl text-foreground-950">
                内容原则
              </h2>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                这些原则写下来，是为了提醒我们自己：整理信息的边界在哪里。
              </p>
            </div>
            <ol className="lg:col-span-7 space-y-4">
              {principles.map((p, i) => (
                <li
                  key={p}
                  className="flex gap-4 p-5 rounded-lg border border-background-200 bg-background-50"
                >
                  <span className="shrink-0 w-8 h-8 rounded-md bg-primary-500 text-background-50 text-sm font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{p}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="px-4 md:px-8 py-14 md:py-20 bg-background-100">
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-7 rounded-full bg-primary-500" />
            <h2 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
              读者口碑
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {voices.map((v) => (
              <blockquote
                key={v.name}
                className="p-6 rounded-lg bg-background-50 border border-background-200 flex flex-col"
              >
                <i className="ri-double-quotes-l text-3xl text-primary-300" />
                <p className="mt-3 text-sm text-foreground-700 leading-relaxed flex-1">{v.text}</p>
                <footer className="mt-5 pt-4 border-t border-background-200 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-secondary-200 text-secondary-800 flex items-center justify-center">
                    <i className="ri-user-smile-line" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{v.name}</span>
                    <span className="block text-xs text-foreground-500">{v.role}</span>
                  </span>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-8 pb-14 md:pb-20">
          <div className="rounded-lg bg-foreground-950 p-6 md:p-10 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <h2 className="font-heading font-black text-2xl md:text-3xl text-background-50">
                联系我们
              </h2>
              <p className="mt-3 text-sm md:text-base text-background-300 leading-relaxed max-w-xl">
                对栏目设置有建议，或发现内容需要更正，欢迎随时来信。我们会在三个工作日内回复。
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
              <div className="flex items-center gap-3 px-5 py-4 rounded-lg bg-background-50/10 border border-background-50/20">
                <i className="ri-mail-line text-xl text-background-50" />
                <span className="text-sm text-background-100">heyliao@example.com</span>
              </div>
              <Link
                to="/faq"
                className="inline-flex items-center justify-center gap-2 px-5 py-4 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-question-answer-line" />
                先看常见问题
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}